import { formatInTimeZone } from 'domains/dates/format';
import { DAY_MONTH_YEAR, TIME_ONLY } from 'utils/date';
import {
  Callout,
  DateRadioChipGroup,
  FormLayout,
  Heading,
  Link,
  LoadingSpinner,
  Paragraph,
  RadioChipGroup,
  RadioChipOptions,
  SubmitButton,
} from '@expressable/syntax-ds';
import { formatPhoneNumber } from 'utils/phoneNumber';
import { PageSizeConfig } from 'components/CalendarPicker/CalendarPageConfig';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Reschedule } from 'domains/direct-reschedule/types';
import { UseControllerReturn } from 'react-hook-form';

type RescheduleUiProps = {
  loading?: boolean;
  onChangeDay: (dayIndex: number) => void;
  onLoadMoreChipsCallback: (() => Promise<void>) | undefined;
  rescheduleInformation?: Reschedule;
  selectedDayIndex: number | undefined;
  currentSlots: Date[] | undefined;
  dayOptions: RadioChipOptions;
  timeSlot: Date | undefined;
  setTimeSlot: (slot: Date | undefined) => void;
  therapistPhoneNumberData: { phoneNumber: string } | undefined;
  displayTimezone: { value: string; shortName: string; label: string } | undefined;
  dayController: UseControllerReturn;
  timeController: UseControllerReturn;
  formatInTimezone: (date: Date, format: string) => string;
  availableTimesQuery: { isFetching: boolean };
};

const RescheduleDirectFormUI = (props: RescheduleUiProps) => {
  const {
    loading,
    onChangeDay,
    onLoadMoreChipsCallback,
    rescheduleInformation,
    selectedDayIndex,
    currentSlots,
    dayOptions,
    timeSlot,
    setTimeSlot,
    therapistPhoneNumberData,
    displayTimezone,
    dayController,
    timeController,
    formatInTimezone,
    availableTimesQuery,
  } = props;

  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="absolute inset-0 flex justify-center items-center w-full">
        <LoadingSpinner data-testid="reschedule-direct-form-ui-loader" />
      </div>
    );
  }

  return (
    <>
      <FormLayout.Fields>
        <div>
          <Heading level="h1">
            {t('reschedule.mainHeading', {
              date: formatInTimeZone(
                new Date(rescheduleInformation?.originalAcuityAppointment?.datetime ?? ''),
                displayTimezone?.value ?? '',
                DAY_MONTH_YEAR,
              ),

              time: formatInTimeZone(
                rescheduleInformation?.originalAcuityAppointment?.datetime ?? '',
                displayTimezone?.value ?? '',
                TIME_ONLY,
              ),
              timezone: displayTimezone?.shortName,
            })}
          </Heading>

          <Paragraph noDefaultMargins size="lg" variant="primary">
            {t('reschedule.subHeading', {
              timezone: displayTimezone?.label,
            })}
          </Paragraph>
        </div>

        <div className="mt-20 mb-20">
          <Callout type="info">{t('reschedule.infoCard')}</Callout>
        </div>

        {displayTimezone && (
          <>
            <DateRadioChipGroup
              startIndex={selectedDayIndex ?? 0}
              htmlName="daypicker"
              label={t('reschedule.DateRadioChipGroup.label')}
              chips={dayOptions}
              variant="carousel"
              value={`${selectedDayIndex}`}
              pageSize={PageSizeConfig}
              onChange={e => {
                const dayPickedIndex = parseInt(e.target.value, 10);
                dayController.field.onChange(dayPickedIndex);
                onChangeDay(dayPickedIndex);
              }}
              disabled={loading}
              dateFormatFn={format}
              loading={availableTimesQuery.isFetching}
              onLoadMore={onLoadMoreChipsCallback}
              name={`dayIndex`}
            />

            {Boolean(currentSlots?.length) && (
              <>
                <RadioChipGroup
                  htmlName="timeslot"
                  label={t('reschedule.RadioChipGroup.label')}
                  pageSize={PageSizeConfig}
                  chips={(currentSlots ?? []).map((slot, index) => ({
                    value: `${index}`,
                    label: formatInTimezone(slot, 'h:mm a'),
                  }))}
                  variant="standard"
                  onChange={e => {
                    const timePickedIndex = parseInt(e.target.value);
                    timeController.field.onChange(timePickedIndex);
                    setTimeSlot(currentSlots?.[timePickedIndex]);
                  }}
                  disabled={loading}
                  value={timeSlot ? `${currentSlots?.indexOf(timeSlot)}` : undefined}
                  name={`timeIndex`}
                />
              </>
            )}
          </>
        )}
      </FormLayout.Fields>

      {therapistPhoneNumberData?.phoneNumber && (
        <Callout type="default" className="w-full">
          {t('reschedule.confirm.callout')}{' '}
          <Link external={true} to={`tel:${therapistPhoneNumberData.phoneNumber}`}>
            {formatPhoneNumber(therapistPhoneNumberData.phoneNumber)}
          </Link>
        </Callout>
      )}

      <FormLayout.GlobalErrors message={t('intake.form.validationErrors')} />

      <FormLayout.CTA>
        <SubmitButton variant="primary" size="lg">
          {t('reschedule.submit')}
        </SubmitButton>
      </FormLayout.CTA>
    </>
  );
};

export default RescheduleDirectFormUI;
